// src/components/project/InternalTimeEntriesView.tsx
// VIB-795: Read-only timer-liste for internprosjekt.
// VIB-1208: gruppert per oppgave i stedet for
// en flat liste — «Uten oppgave»-bucket for eldre/ugrupperte føringer.
import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useInternalTimeEntries, groupInternalTimeEntriesByTask } from "@/hooks/use-internal-time-entries";
import { InternalTimeEntriesGroupRow } from "./InternalTimeEntriesGroupRow";

interface Props {
  projectId: string;
  viewerUserId: string | null;
  canSeeAll: boolean;
}

function fmtHours(seconds: number): string {
  return `${(seconds / 3600).toFixed(1).replace(".", ",")} t`;
}

export function InternalTimeEntriesView({ projectId, viewerUserId, canSeeAll }: Props) {
  const { data: entries = [], isLoading } = useInternalTimeEntries({ projectId, viewerUserId, canSeeAll });
  const totalSec = useMemo(() => entries.reduce((s, e) => s + e.duration_seconds, 0), [entries]);

  // VIB-1208: gruppert per oppgave, ekspanderbart til enkeltføringer.
  const groups = useMemo(() => groupInternalTimeEntriesByTask(entries), [entries]);

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-display tracking-wide">Timer</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {canSeeAll ? "Alle føringer på prosjektet" : "Dine egne føringer"}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Totalt</div>
            <div className="text-lg font-bold tabular-nums">{fmtHours(totalSec)}</div>
          </div>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Laster …</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Ingen føringer ennå
          </p>
        ) : (
          <div className="rounded-lg border border-border/50 divide-y divide-border/50">
            {groups.map((g) => (
              <InternalTimeEntriesGroupRow key={g.budgetLineId ?? "ungrouped"} group={g} canSeeAll={canSeeAll} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
